import { useState, type ReactElement } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { HighlightBox } from '@/components/shared/HighlightBox';
import { useToast } from '@/hooks/use-toast';
import { usePluginApiKeys } from '@/hooks/api/useApiKeys';
import { API_BASE } from '@/lib/api-client';
import { getSessionToken } from '@descope/react-sdk';
import { pluginDownloadPath } from '@kirin/api-client/api/organizations';
import { AlertTriangle, Download } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/api/queryKeys';
import { triggerBlobDownload } from '@/lib/download-utils';
import { isPluginKeyActive } from '@/lib/plugin-utils';
import { PluginConfigDetails } from '@/pages/organization/components/PluginConfigDetails';
import { useIsSupport } from '@/hooks/useRoleCheck';

interface ClaudeDesktopPluginStepsProps {
  organizationId: string;
}

/**
 * The "Install the Kirin Hook Plugin for Claude Desktop" block: mints a fresh
 * plugin API key, embeds it + the backend URL into the plugin zip, and lets the
 * user download it. Org-scoped only — rendered inside the Kirin Agent setup
 * frame in the organization view (the personal view has no plugin flow).
 */
export function ClaudeDesktopPluginSteps({
  organizationId,
}: ClaudeDesktopPluginStepsProps): ReactElement {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: pluginKeys = [] } = usePluginApiKeys(organizationId);
  // Downloading a fresh plugin only starts a 7-day expiry on active keys that
  // aren't already expiring. Keys with an expiry already set (or revoked ones)
  // are unaffected, so only these count toward the fleet-wide consequence.
  const keysToExpire = pluginKeys.filter(
    key => isPluginKeyActive(key) && !key.expiresAt
  );
  const keysToExpireCount = keysToExpire.length;
  const willExpireKeys = keysToExpireCount > 0;
  const { isSupport, isLoading: isSupportLoading } =
    useIsSupport(organizationId);

  const performDownload = async (): Promise<void> => {
    setDownloading(true);
    try {
      const token = await getSessionToken();
      const response = await fetch(
        `${API_BASE}${pluginDownloadPath(organizationId)}`,
        {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }
      const blob = await response.blob();
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      const filename = `kirin-claude-plugin.${yyyy}-${mm}-${dd}.zip`;
      triggerBlobDownload(blob, filename);
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.pluginApiKeys(organizationId),
      });
      toast({
        title: 'Plugin downloaded',
        description:
          'Upload the zip to Anthropic via your org plugin settings.',
      });
    } catch (error) {
      toast({
        title: 'Could not download plugin',
        description:
          error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  const onClickDownload = (): void => {
    if (willExpireKeys) {
      setConfirmOpen(true);
      return;
    }
    void performDownload();
  };

  const isSingleKey = keysToExpireCount === 1;
  const dialogTitle = isSingleKey
    ? 'Expire your active plugin key?'
    : `Expire your ${keysToExpireCount} active plugin keys?`;
  const confirmLabel = isSingleKey
    ? 'Yes, expire my plugin key in 7 days'
    : 'Yes, expire existing plugin keys in 7 days';

  return (
    <div>
      <h3 className="text-base sm:text-lg font-semibold mb-4 break-words">
        Install the Kirin Hook Plugin for Claude Desktop
      </h3>
      <p className="text-sm text-muted-foreground mb-4">
        Anthropic's Claude Desktop runs hook plugins on Anthropic-hosted VMs, so
        the plugin must talk to Kirin directly. Pressing the button below mints
        a fresh API key, embeds it (and the backend URL) into the plugin zip,
        and stores only the hashed key on our side.
      </p>
      <p className="text-sm text-muted-foreground mb-2">
        The downloaded plugin will be configured with:
      </p>
      <PluginConfigDetails organizationId={organizationId} className="mb-4" />
      <Button
        onClick={onClickDownload}
        disabled={downloading || isSupport || isSupportLoading}
      >
        <Download className="h-4 w-4 mr-2" />
        {downloading ? 'Preparing zip…' : 'Download Plugin'}
      </Button>
      <HighlightBox variant="info" padding="lg" className="mt-4">
        <div className="text-sm">
          <div className="font-medium mb-2">After downloading</div>
          <div className="text-muted-foreground">
            Upload <code>kirin-claude-plugin.zip</code> to your Anthropic
            organization's plugin registry. Once it's enabled, every Claude
            Desktop session will route hook events through Kirin.
          </div>
        </div>
      </HighlightBox>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              {dialogTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Downloading a new plugin mints a fresh API key and starts a 7-day
              expiration on every key currently in use.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <HighlightBox variant="warning" padding="lg" role="alert">
            <div className="flex items-start gap-2 text-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="text-amber-800 dark:text-amber-200">
                {isSingleKey ? (
                  <>
                    Your{' '}
                    <span className="font-semibold">Kirin Plugin API key</span>{' '}
                    currently deployed across your organization will stop
                    working in 7 days.
                  </>
                ) : (
                  <>
                    All{' '}
                    <span className="font-semibold">
                      {keysToExpireCount} Kirin Plugin API keys
                    </span>{' '}
                    currently deployed across your organization will stop
                    working in 7 days.
                  </>
                )}{' '}
                Every device still running the old plugin loses Kirin protection
                after the grace period until it is updated with the new
                download.
                <ul className="mt-2 space-y-1">
                  {keysToExpire.map(key => (
                    <li key={key.id} className="font-mono text-xs break-all">
                      {key.keyPrefix}…
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </HighlightBox>
          <div className="text-sm">
            <div className="text-muted-foreground mb-2">
              The downloaded plugin will be configured with:
            </div>
            <PluginConfigDetails organizationId={organizationId} />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={downloading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white shadow-sm hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60"
              onClick={() => {
                setConfirmOpen(false);
                void performDownload();
              }}
              disabled={downloading}
            >
              {confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
